"use client"

import type React from "react"

import { useState } from "react"
import ViewMath from 'components/HHQComponents/ViewMath';
import { TestDetailQuizModel } from 'models/test-detail.model';
import { Button } from "primereact/button";
import './index.css';

export interface DragDropItem {
  id: string
  text: string
}

interface PropTypes {
    leftColumn: DragDropItem[];
    rightColumn: DragDropItem[];
    question: TestDetailQuizModel;
    handleUpdateQuestions?: (newValue: TestDetailQuizModel) => void;
    canDrag?: boolean;
    showTruth?: boolean;
}

const ArrowRightIcon = () => (
  <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

export default function DragDropComponent(prog: PropTypes) {
  const [leftColumn, setLeftColumn] = useState<DragDropItem[]>(prog.leftColumn)
  const handleUpdateQuestions = prog.handleUpdateQuestions
  const [rightColumn, setRightColumn] = useState<DragDropItem[]>(prog.rightColumn)
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [draggedFromColumn, setDraggedFromColumn] = useState<"left" | "right" | null>(null)
  const [showAnswer, setShowAnswer] = useState(false);
  const [originalState, setOriginalState] = useState<{
    leftColumn: DragDropItem[]
    rightColumn: DragDropItem[]
  } | null>(null)
  const [hasValidDrop, setHasValidDrop] = useState(false)
  let allAnwserTruth = true;

  const handleDragStart = (e: React.DragEvent, itemId: string, column: "left" | "right") => {
    setDraggedItem(itemId)
    setDraggedFromColumn(column)
    setHasValidDrop(false)

    // Lưu trạng thái ban đầu để có thể restore
    setOriginalState({
      leftColumn: [...leftColumn],
      rightColumn: [...rightColumn],
    })

    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", itemId)
  }

  const handleDragEnter = (e: React.DragEvent, targetItemId: string, targetColumn: "left" | "right") => {
    e.preventDefault()

    if (!draggedItem || !draggedFromColumn || draggedFromColumn !== targetColumn || draggedItem === targetItemId) {
      return
    }

    // Tự động swap vị trí
    const sourceColumn = targetColumn === "left" ? leftColumn : rightColumn
    const setSourceColumn = targetColumn === "left" ? setLeftColumn : setRightColumn

    const draggedIndex = sourceColumn.findIndex((item) => item.id === draggedItem)
    const targetIndex = sourceColumn.findIndex((item) => item.id === targetItemId)

    if (draggedIndex === -1 || targetIndex === -1) return

    const newColumn = [...sourceColumn]
    const [draggedItemData] = newColumn.splice(draggedIndex, 1)
    newColumn.splice(targetIndex, 0, draggedItemData)

    setSourceColumn(newColumn)
    setHasValidDrop(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setHasValidDrop(true)
  }

  const getArrowClass = (index: number): string => {
    if (prog.canDrag !== false) return ""; // Nếu vẫn cho kéo, không hiển thị đánh giá

    const correctAnswer = prog.question.testQuestionAnswers?.find(a => a.order === index);
    const userAnswer = prog.question.userAnswer?.[index];

    if (!correctAnswer || !correctAnswer.answerLeft?.trim()) return "hidden"; // Không hiển thị

    const correctHTML = correctAnswer.answerRight?.trim() || "";
    const userHTML = userAnswer?.trim() || "";

    if (correctHTML === userHTML) 
    {
      return "arrow-correct"; // xanh lá
    }
    else 
    {
      allAnwserTruth = false; // Nếu có ít nhất một đáp án sai, đánh dấu là không đúng
      return "arrow-wrong"; // đỏ
    }
    
  };

  const toggleShowAnswer = () => {
    if (!showAnswer) {
      // Lưu trạng thái hiện tại
      setOriginalState({
        leftColumn,
        rightColumn
      });

      const correctAnswers = prog.question.testQuestionAnswers?.filter(a => a.answerLeft?.trim()) || [];

      const left: DragDropItem[] = correctAnswers.map((item, i) => ({
        id: `${i}`,
        text: item.answerLeft || '',
      }));

      const right: DragDropItem[] = correctAnswers.map((item, i) => ({
        id: `right-${i}`,
        text: item.answerRight || '',
      }));

      setLeftColumn(left);
      setRightColumn(right);
      setShowAnswer(true);
    } else if (originalState) {
      setLeftColumn(originalState.leftColumn);
      setRightColumn(originalState.rightColumn);
      setShowAnswer(false);
    }
  };


  const handleDragEnd = (e: React.DragEvent) => {
    if (!hasValidDrop && originalState) {
      setLeftColumn(originalState.leftColumn);
      setRightColumn(originalState.rightColumn);
    }

    // ✅ Mapping theo thứ tự hàng hiển thị: leftColumn[i].id → rightColumn[i].text
    if (handleUpdateQuestions) {
      const newUserAnswer: string[] = [...(prog.question.userAnswer || [])];

      leftColumn.forEach((leftItem, i) => {
        const index = parseInt(leftItem.id); // đảm bảo id là số
        newUserAnswer[index] = rightColumn[i]?.text || '';
      });

      const updatedQuestion: TestDetailQuizModel = {
        ...prog.question,
        userAnswer: newUserAnswer,
      };

      handleUpdateQuestions(updatedQuestion);
    }

    // Reset states
    setDraggedItem(null);
    setDraggedFromColumn(null);
    setOriginalState(null);
    setHasValidDrop(false);
  };

  const renderColumn = (items: DragDropItem[], column: "left" | "right", title: string) => (
    <div className="column">
      <div
        className="drop-zone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="items-container">
          {items.filter(x=> x.text.trim() != '').map((item, index) => {
            let itemColor = "item-blue";
              if(column === "right" && prog.canDrag === false && !showAnswer){
                const correct = prog.question.testQuestionAnswers?.find(a => a.order === index);
                const userAns = prog.question.userAnswer?.[index]?.trim();
                const correctAns = correct?.answerRight?.trim();
                if(!correct || correct?.answerLeft == '')
                {
                  itemColor = "item-gray"; // Không có đáp án trái
                }
                else if(correct?.answerLeft && correctAns !== userAns) {
                    itemColor = "item-red"; // Đáp án sai
                }
                else {
                  itemColor = "item-green"; // Đáp án đúng
                }
              }
            return (<div
              key={item.id}
              draggable={prog.canDrag !== false}
              onDragStart={prog.canDrag === false ? undefined : (e) => handleDragStart(e, item.id, column)}
              onDragEnter={prog.canDrag === false ? undefined : (e) => handleDragEnter(e, item.id, column)}
              onDragOver={prog.canDrag === false ? undefined : handleDragOver}
              onDragEnd={prog.canDrag === false ? undefined : handleDragEnd}
              className={`item ${itemColor}
                ${prog.canDrag === false ? 'drag-disabled' : ''}
                ${draggedItem === item.id ? "item-dragging" : ""}
                ${draggedItem && draggedFromColumn === column && draggedItem !== item.id ? "item-drag-target" : ""}
              `}
            >
              <ViewMath>
                <span className="item-text"
                  dangerouslySetInnerHTML={{
                    __html: item.text.trim(),
                  }}
                />
              </ViewMath>
            </div>)
          })}
        </div>
      </div>
    </div>
  )

  return (
    <div className="main-container">
      <div className="card">
        <div className="card-content">
            <div className="header">
              <p className="subtitle">
                Kéo thả để sắp xếp các câu giữa hai cột. Các đáp án đúng phải nằm trên cùng một dòng.
              </p>
            </div>
            <div className="columns-container">
              {renderColumn(leftColumn, "left", "Cột trái")}

              {/* Arrows section */}
              <div className="arrows-container">
                <div className="arrows-list">
                  {leftColumn.map((_, index) => (
                    <div key={index} className={`arrow-item ${getArrowClass(index)}`}>
                      <ArrowRightIcon />
                    </div>
                  ))}
                </div>
              </div>
              
              {renderColumn(rightColumn, "right", "Cột phải")}
            </div>
            {(prog.canDrag === false) && ( 
              <div className="instructions">
                <div className="columns-container">
                  <div className="column">
                    <ul className="instructions-list">
                      <li className="instructions-item"><span className="arrow-item arrow-correct"><ArrowRightIcon /> Đáp án đúng</span></li>
                      <li className="instructions-item"><span className="arrow-item arrow-wrong"><ArrowRightIcon /> Đáp án sai</span></li>
                    </ul>
                  </div>
                  {prog.showTruth && (!allAnwserTruth || showAnswer) && (
                    <div className="column" style={{ textAlign: 'right', flexDirection: 'column' }}>
                      <Button
                        label={showAnswer ? "Xem bài làm" : "Xem đáp án"}
                        className="btn-default pl-4 pr-4 mb-4 p-button-sm"
                        onClick={toggleShowAnswer}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
